import React, { useEffect, useState, Fragment } from 'react';
import { Grid, Typography, Button, Colors } from '@inplayer-org/inplayer-ui';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import moment, { Moment } from 'moment';
import { MdInfoOutline } from 'react-icons/md';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';

// types
import RootState from 'ducks/RootState';
import { SeasonPhase } from 'ducks/merchantDucks/items/types';

// actions
import {
  getSeasonPhases,
  updateSeasonPhase,
  createSeasonPhase,
  loadAllAccessFees,
} from 'ducks/merchantDucks/items/actions';

// utils
import { convertArrayToCamelCase } from 'utils/helpers';
import accessCurrencies from 'data/accessCurrencies';

// components
import { TooltipStyled } from 'pages/MerchantPages/Assets/Vouchers/shared/sharedComponents';
import SingleDayPicker from 'components/SingleDayPicker';
import TimeHourPicker from 'components/TimeHourPicker';
import FieldError from 'components/Fields/FieldError';
import Input from 'components/Fields/Input';
import Select from 'components/Fields/Select';
import { BlueTypography } from 'components/SharedStyledElements';
import ButtonTooltip from 'components/ButtonTooltip';
import { snakeCase } from 'lodash';
import SeasonPhasesTable from './SeasonPhasesTable';

// validation
import { seasonValidationSchema } from '../validationSchema';

import { seasonAccessDateFields, seasonPriceFields, seasonInvalidDatesMessage } from './seasonSubscriptionConfig';

const { Cell, Container } = Grid;

const StyledCell = styled(Cell)`
  box-shadow: 0 0 4px 1px ${Colors.gray};
  border-radius: 5px;
  padding: 1.5rem;
`;

const StyledTypography = styled(BlueTypography)`
  margin-top: 1rem;
`;

const StyledError = styled.span`
  margin-top: 0.25rem;
  display: block;
  color: ${Colors.red};
`;

export enum SeasonPhaseStatus {
  lockedIn = 'locked-in',
  pending = 'pending',
}

interface FormValues {
  startsAt: Moment;
  expiresAt: Moment;
  anchorDate: Moment;
  currentPrice: string;
  seasonPrice: string;
  currency: string;
  status?: string;
}

export interface SeasonPhaseType extends FormValues {
  id: number;
}

const initialValues: FormValues = {
  startsAt: moment(),
  expiresAt: moment(),
  anchorDate: moment(),
  currentPrice: '',
  seasonPrice: '',
  currency: 'EUR',
  status: '',
};

interface Props {
  isReadOnly: boolean;
}

const SeasonSubscriptionInfo = ({ isReadOnly }: Props) => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const seasonPhases = useSelector((state: RootState) => state.items.itemDetails.seasonPhases);
  const accessFeeId = useSelector((state: RootState) => state.items.itemDetails.selectedFeeId);

  const [showPhaseInfo, setShowPhaseInfo] = useState(false);
  const [formValues, setFormValues] = useState(initialValues);
  const [isEditMode, setIsEditMode] = useState(false);
  const [disabledEdit, setDisabledEdit] = useState(false);
  const [selectedPhaseId, setSelectedPhaseId] = useState(0);

  const [seasonPhasesList, setSeasonPhasesList] = useState<Array<SeasonPhaseType>>([]);

  const [phasesErrorMessage, setPhasesErrorMessage] = useState('');

  useEffect(() => {
    dispatch(getSeasonPhases(Number(id), accessFeeId));
  }, []);

  useEffect(() => {
    const newList = convertArrayToCamelCase<SeasonPhase, SeasonPhaseType>(seasonPhases).map((item: any) => {
      return {
        ...item,
        startsAt: moment.unix(item.startsAt),
        expiresAt: moment.unix(item.expiresAt),
        anchorDate: moment.unix(item.anchorDate),
      };
    });
    setSeasonPhasesList(newList);
  }, [seasonPhases]);

  const displayErrorMessage = (message: string) => {
    setPhasesErrorMessage(message);
    setTimeout(() => {
      setPhasesErrorMessage('');
    }, 5000);
  };

  const hidePhaseInfo = () => {
    setShowPhaseInfo(!showPhaseInfo);
    setFormValues(initialValues);
    setPhasesErrorMessage('');
  };

  const resetFormData = (resetForm: Function) => () => {
    resetForm();
    hidePhaseInfo();
  };

  const isLockedPhase = (status?: string) => status === SeasonPhaseStatus.lockedIn;

  const isPastPhase = () => {
    const { status, expiresAt } = formValues;
    return isLockedPhase(status) && moment().isAfter(expiresAt);
  };

  const isFieldDisabled = (canBeEdited?: boolean) => {
    return isReadOnly || (disabledEdit && !canBeEdited) || (isEditMode && isPastPhase());
  };

  const isCurrentPhase = (row: SeasonPhaseType) => {
    const { status, expiresAt } = row;
    return isLockedPhase(status) && moment().isBefore(expiresAt);
  };

  const handleShowPhaseInfo = (row?: SeasonPhaseType) => {
    setShowPhaseInfo(true);
    setIsEditMode(!!row);
    setDisabledEdit(!!row && isLockedPhase(row.status));
    if (row) {
      setSelectedPhaseId(row.id);
      setFormValues(row);
    } else {
      setSelectedPhaseId(-1);
      setFormValues(initialValues);
    }
  };

  const validateData = (data: SeasonPhaseType) => {
    let isValid = true;
    const { startsAt, expiresAt } = data;

    const filtered = [...(isEditMode ? seasonPhasesList.filter((x) => x.id !== data.id) : seasonPhasesList)];
    const itemIndex = filtered.filter((item) => item.expiresAt.isBefore(startsAt)).length;

    const phases = [...filtered.slice(0, itemIndex), { startsAt, expiresAt }, ...filtered.slice(itemIndex)];
    phases.forEach((item, index) => {
      if (index !== 0) {
        const previousItem = phases[index - 1];
        const nextItem = phases[index + 1];
        if (item.startsAt.isBefore(previousItem.expiresAt) || (nextItem && item.expiresAt.isAfter(nextItem.startsAt))) {
          displayErrorMessage('The new phase is overlapping with an existing one.');
          isValid = false;
        }
      }
    });
    return isValid;
  };

  const onFormSubmit = async (values: FormValues) => {
    const data = { ...values, id: selectedPhaseId };
    let isValid = true;
    if (!isLockedPhase(values.status) || isCurrentPhase(data)) {
      isValid = validateData(data);
      if (isValid) {
        if (isEditMode) {
          await dispatch(updateSeasonPhase(Number(id), accessFeeId, data));
        } else {
          await dispatch(createSeasonPhase(Number(id), accessFeeId, data));
        }
        hidePhaseInfo();
        dispatch(loadAllAccessFees(Number(id)));
      }
    }
    if (isValid) hidePhaseInfo();
  };

  const renderSeasonDates = () => {
    return seasonAccessDateFields.map(({ name, title, tooltipContent }) => {
      return (
        <Fragment key={name}>
          <Cell width={6} key={name}>
            <Container>
              <Cell width={12}>
                <Typography variant="p">{title}</Typography>
              </Cell>
              <Field name={name}>
                {({ field: childField, form: childForm }: any) => (
                  <>
                    <Cell width={4} middle>
                      <SingleDayPicker
                        date={childField.value}
                        handleDateChange={async (date: Moment) => {
                          await childForm.setFieldValue(childField.name, date);
                          await childForm.setFieldTouched(childField.name, true);
                        }}
                        disabled={isReadOnly || disabledEdit}
                      />
                    </Cell>
                    <Cell width={4} middle>
                      <TimeHourPicker
                        handleTimeChange={async (time: Moment) => {
                          await childForm.setFieldValue(childField.name, time);
                          await childForm.setFieldTouched(childField.name, true);
                        }}
                        time={childField.value}
                        disabled={isReadOnly || disabledEdit}
                      />
                    </Cell>
                    <Cell width={1} middle>
                      <Container columns={1}>
                        <Cell width={1} middle>
                          <TooltipStyled
                            tag={`tooltip_calendar_${snakeCase(title)}`}
                            placement="top"
                            content={tooltipContent}
                          >
                            <MdInfoOutline />
                          </TooltipStyled>
                        </Cell>
                      </Container>
                    </Cell>
                  </>
                )}
              </Field>
            </Container>
            <FieldError name={name} />
          </Cell>
        </Fragment>
      );
    });
  };

  const renderSeasonFields = () => {
    return seasonPriceFields.map(
      ({ name, placeholder, title, tooltipContent, canBeEdited, tag, dropdownTag, tooltipTag }) => {
        return (
          <Fragment key={name}>
            <Cell width={12}>
              <span>{title}</span>
              <TooltipStyled tag={tooltipTag} placement="right" content={tooltipContent}>
                <MdInfoOutline />
              </TooltipStyled>
            </Cell>
            <Cell width={6}>
              <Field
                tag={tag}
                name={name}
                component={Input}
                placeholder={placeholder}
                disabled={isFieldDisabled(canBeEdited)}
              />
              <FieldError name={name} />
            </Cell>
            <Cell width={6}>
              <Field name="currency">
                {({ field, form, meta }: FieldProps) => (
                  <Select
                    tag={dropdownTag}
                    field={field}
                    form={form}
                    meta={meta}
                    value={field.value}
                    placeholder="currency"
                    options={accessCurrencies}
                    disabled={isReadOnly || disabledEdit}
                  />
                )}
              </Field>
              <FieldError name="currency" />
            </Cell>
          </Fragment>
        );
      }
    );
  };

  return (
    <>
      {showPhaseInfo ? (
        <Formik
          enableReinitialize
          initialValues={formValues}
          validationSchema={seasonValidationSchema}
          onSubmit={onFormSubmit}
        >
          {({ resetForm, isValid, isSubmitting, submitForm, errors }: FormikProps<FormValues>) => (
            <StyledCell width={12}>
              <Container>
                <Cell width={12}>
                  <StyledTypography variant="h6">Season duration</StyledTypography>
                </Cell>
                {renderSeasonDates()}
                <Cell width={12}>
                  <StyledTypography variant="h6">Pricing</StyledTypography>
                </Cell>
                {renderSeasonFields()}
                <Cell width={12}>
                  <StyledError>{phasesErrorMessage}</StyledError>
                </Cell>
                <Cell width={2}>
                  <Button fullWidth buttonModifiers={['buttonLink']} type="button" onClick={resetFormData(resetForm)}>
                    Cancel
                  </Button>
                </Cell>
                <Cell width={2}>
                  <ButtonTooltip content={errors.startsAt && seasonInvalidDatesMessage} width="100%">
                    <Button fullWidth type="button" onClick={submitForm} disabled={!isValid || isSubmitting}>
                      Save
                    </Button>
                  </ButtonTooltip>
                </Cell>
              </Container>
            </StyledCell>
          )}
        </Formik>
      ) : (
        <>
          <Cell width={12}>
            <BlueTypography variant="h6">Season phases</BlueTypography>
          </Cell>
          <Cell width={12}>
            <SeasonPhasesTable
              seasonPhases={seasonPhasesList}
              onEditClick={(row: SeasonPhaseType) => handleShowPhaseInfo(row)}
            />
          </Cell>
          <Cell width={3}>
            <Button tag="button_add_new_phase" fullWidth type="button" onClick={() => handleShowPhaseInfo()}>
              Add new phase
            </Button>
          </Cell>
        </>
      )}
    </>
  );
};

export default SeasonSubscriptionInfo;
